import { pick } from 'lodash';
import React, { useEffect } from 'react';
import { SurveyTaskProgressStates } from '../../../../../common/src/domain/survey/surveyTask/SurveyTaskProgressStates';
import { SurveyStates } from '../../../../../common/src/enums/SurveyStates';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchGet, openApiFetchPut } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import {
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelInput,
    EditPanelRow,
    PanelBody,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
} from '../../elements';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { SurveyEditCollapsiblePanel } from './SurveyEditCollapsiblePanel';
import { SurveyTaskProgressStatusLabel } from './SurveyTaskProgressStatusLabel';

function SurveyTaskProgressStatusSelect(props: { value: string; onChange: (newValue: string) => void }) {
    const { value, onChange } = props;
    const translate = useTranslate();
    return (
        <>
            <label className="field select">
                <select
                    id="condition-select"
                    name="condition "
                    className={`survey-task-progress-label-${value}`}
                    style={{ color: '#fff' }}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option style={{ color: '#000' }} value={SurveyTaskProgressStates.Open}>
                        {translate('surveyTaskProgressStatus.open')}
                    </option>
                    <option style={{ color: '#000' }} value={SurveyTaskProgressStates.InProgress}>
                        {translate('surveyTaskProgressStatus.inProgress')}
                    </option>
                    <option style={{ color: '#000' }} value={SurveyTaskProgressStates.Completed}>
                        {translate('surveyTaskProgressStatus.completed')}
                    </option>
                    <option style={{ color: '#000' }} value={SurveyTaskProgressStates.NotApplicable}>
                        {translate('surveyTaskProgressStatus.notApplicable')}
                    </option>
                </select>
                <i className="arrow" />
            </label>
        </>
    );
}

export function SurveyEditWorkflowChecklistEdit(props: { surveyId: string; surveyStatus: string; onClose: () => void }) {
    const { surveyId, surveyStatus } = props;
    const translate = useTranslate();

    const [originalTasks, setOriginalTasks] = React.useState<
        { id: string; name: string; progressStatus: SurveyTaskProgressStates; remark: string | null }[]
    >([]);
    const [editableTasks, setEditableTasks] = React.useState<
        { id: string; name: string; progressStatus: SurveyTaskProgressStates; remark: string }[]
    >([]);

    useEffect(() => {
        async function fetchTasks() {
            const response = await openApiFetchGet('/api/surveys/{surveyId}/tasks', { surveyId, surveyStatus });
            setOriginalTasks(response.items.map((i) => ({ id: i.id, name: i.name, progressStatus: i.progressStatus, remark: i.remark })));
            setEditableTasks(response.items.map((i) => ({ id: i.id, name: i.name, progressStatus: i.progressStatus, remark: i.remark ?? '' })));
        }
        void fetchTasks();
    }, [surveyStatus, surveyId]);

    function onChange(taskId: string, remark: string, progressStatus: SurveyTaskProgressStates) {
        const newTasks = editableTasks.map((t) => {
            if (t.id === taskId) {
                return { ...t, remark, progressStatus };
            }
            return t;
        });
        setEditableTasks(newTasks);
    }

    async function save() {
        const tasksToUpdate = editableTasks
            .filter((t) => {
                const originalTask = originalTasks.find((ot) => ot.id === t.id)!;
                return originalTask.progressStatus !== t.progressStatus || (originalTask.remark ?? '') !== t.remark;
            })
            .map((t) => pick(t, ['id', 'remark', 'progressStatus']));

        await openApiFetchPut('/api/surveys/{surveyId}/tasks', { surveyId }, { items: tasksToUpdate });
        props.onClose();
    }

    return (
        <>
            <PanelTable>
                <TableHead>
                    <TableHeadRow>
                        <TableHeader>{translate('workflow.task')}</TableHeader>
                        <TableHeader>{translate('common.progress')}</TableHeader>
                        <TableHeader>{translate('common.remark')}</TableHeader>
                        <TableHeader></TableHeader>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {editableTasks.map((item) => (
                        <TableRow key={item.id}>
                            <TableData>{item.name}</TableData>
                            <TableData>
                                <SurveyTaskProgressStatusSelect
                                    value={item.progressStatus}
                                    onChange={(value) => onChange(item.id, item.remark ?? '', value as SurveyTaskProgressStates)}
                                />
                            </TableData>
                            <TableData>
                                <EditPanelInput
                                    name="asd"
                                    value={item.remark}
                                    handleChange={(value) => onChange(item.id, value, item.progressStatus)}
                                />{' '}
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </PanelTable>
            <EditPanelButtons>
                <EditPanelButton small={true} text={translate('common.cancel')} onClick={props.onClose} />
                <EditPanelButton small={true} primary={true} text={translate('common.save')} onClick={save} />
            </EditPanelButtons>
        </>
    );
}

export function SurveyEditWorkflowChecklistView(props: { surveyId: string; surveyStatus: string; onEditChecklistClicked: () => void }) {
    const { surveyId, surveyStatus } = props;
    const translate = useTranslate();

    const { data: surveyTasks } = useOpenApiFetch('/api/surveys/{surveyId}/tasks', 'get', { surveyId, surveyStatus }, [surveyStatus]);

    if (!surveyTasks || surveyTasks.items.length === 0) {
        return null;
    }
    return (
        <>
            <EditPanelRow>
                <EditPanelCell rowCount={6}>
                    {translate('common.status')}: {translate(`surveyStatus.${props.surveyStatus}`)}
                </EditPanelCell>
            </EditPanelRow>
            <PanelTable>
                <TableHead>
                    <TableHeadRow>
                        <TableHeader>{translate('workflow.task')}</TableHeader>
                        <TableHeader>{translate('common.remark')}</TableHeader>
                        <TableHeader>{translate('workflow.updatedBy')}</TableHeader>
                        <TableHeader>{translate('common.date')}</TableHeader>
                        <TableHeader></TableHeader>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {surveyTasks.items.map((item) => (
                        <TableRow key={item.id}>
                            <TableData>{item.name}</TableData>
                            <TableData>{item.remark}</TableData>
                            <TableData>
                                {item.updatedByUser.id && (
                                    <>
                                        {item.updatedByUser.firstName} {item.updatedByUser.lastName}
                                    </>
                                )}
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={item.updatedOn}></NullableFormattedDate>
                            </TableData>
                            <TableData>
                                <SurveyTaskProgressStatusLabel value={item.progressStatus} />
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </PanelTable>
            <EditPanelButtons>
                <EditPanelButton onClick={props.onEditChecklistClicked} primary text={translate('workflow.editChecklist')} small />
            </EditPanelButtons>
        </>
    );
}

export function SurveyEditWorkflowChecklistStatusOverview(props: { surveyId: string; surveyStatus: SurveyStates }) {
    const { surveyId } = props;
    const [mode, setMode] = React.useState<'view' | 'edit'>('view');

    return (
        <PanelBody isForm={true}>
            {mode === 'view' && (
                <SurveyEditWorkflowChecklistView
                    surveyId={surveyId}
                    surveyStatus={props.surveyStatus}
                    onEditChecklistClicked={() => setMode('edit')}
                />
            )}
            {mode === 'edit' && (
                <SurveyEditWorkflowChecklistEdit surveyId={surveyId} surveyStatus={props.surveyStatus} onClose={() => setMode('view')} />
            )}
        </PanelBody>
    );
}

export function SurveyEditWorkflowChecklistBody(props: { surveyId: string }) {
    const { surveyId } = props;

    const surveyWorkflowStates = [SurveyStates.Open, SurveyStates.Conducting, SurveyStates.Delivery, SurveyStates.Reporting];

    return (
        <PanelBody isForm={true}>
            {surveyWorkflowStates.map((surveyStatus) => (
                <SurveyEditWorkflowChecklistStatusOverview
                    key={surveyStatus}
                    surveyId={surveyId}
                    surveyStatus={surveyStatus}
                ></SurveyEditWorkflowChecklistStatusOverview>
            ))}
        </PanelBody>
    );
}

export function SurveyEditWorkflowChecklist(props: { surveyId: string }) {
    const { surveyId } = props;
    const translate = useTranslate();

    return (
        <SurveyEditCollapsiblePanel headerText={translate('workflow.checklist')} initialIsClosed>
            <SurveyEditWorkflowChecklistBody surveyId={surveyId} />
        </SurveyEditCollapsiblePanel>
    );
}
